/** QuickRouter — Schéma d'un projet de circuit imprimé (validé par zod, partagé client/serveur).
 *
 * Repère : millimètres, origine au coin bas-gauche de la carte, X vers la droite, Y vers
 * le HAUT (convention Gerber/KiCad vue de dessus). Les angles sont en degrés, sens
 * trigonométrique. Couches cuivre : F (dessus) et B (dessous).
 *
 * Un projet = une carte (contour, épaisseur, règles), des composants posés (empreinte +
 * repère + position), une liste d'équipotentielles (nets : « U1.3 », « J2.GND »…), des
 * pistes, des vias, des zones de cuivre et des textes de sérigraphie. Les empreintes
 * propres au projet (créées par l'assistant) sont embarquées dans `footprints`.
 */
import { z } from 'zod';

const num = z.number().finite();
const vec2 = z.tuple([num, num]);

export const LAYERS = ['F', 'B'];
/** Toutes les couches cuivre possibles (6 couches au plus) ; voir shared/layers.js. */
export const CU_LAYERS = ['F', 'In1', 'In2', 'In3', 'In4', 'B'];
const cuLayer = z.enum(['F', 'In1', 'In2', 'In3', 'In4', 'B']);
export const SIDES = ['top', 'bottom'];

export function newId(prefix = 'x') {
  return `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
}

// ── Empreintes ─────────────────────────────────────────────────

export const PadSchema = z.object({
  num: z.string().min(1).max(16),              // numéro de broche (« 1 », « A1 », « EP »)
  name: z.string().max(32).default(''),         // nom fonctionnel (« GND », « IO4 », « A »)
  shape: z.enum(['rect', 'roundrect', 'circle', 'oval']).default('rect'),
  pos: vec2,                                    // centre, repère de l'empreinte
  size: vec2,                                   // largeur × hauteur avant rotation
  rot: num.default(0),
  drill: num.positive().optional(),             // présent = traversant (cuivre sur F et B)
  plated: z.boolean().default(true),            // false = trou non métallisé (fixation)
  layer: z.enum(['F', 'B']).default('F'),       // couche d'un CMS (côté de l'empreinte)
  paste: z.boolean().default(true),
});

export const GraphicSchema = z.object({
  kind: z.enum(['line', 'rect', 'circle', 'poly']),
  pts: z.array(vec2).default([]),               // line/poly : points ; rect : 2 coins ; circle : [centre]
  r: num.nonnegative().optional(),              // rayon d'un cercle
  width: num.positive().default(0.15),
  fill: z.boolean().default(false),
});

export const FootprintSchema = z.object({
  id: z.string().min(1).max(80),
  name: z.string().min(1).max(120),
  category: z.string().max(40).default('Divers'),
  description: z.string().max(4000).default(''),
  refPrefix: z.string().max(6).default('U'),
  pads: z.array(PadSchema).max(400),
  silk: z.array(GraphicSchema).max(400).default([]),
  courtyard: z.tuple([vec2, vec2]).optional(),  // [min, max] — encombrement au sol
  body: z.object({                              // volume 3D (boîte) au-dessus de la carte
    size: z.tuple([num, num, num]),
    offset: vec2.default([0, 0]),
    color: z.string().max(16).default('#222'),
  }).optional(),
  keepouts: z.array(z.object({                  // zones interdites au cuivre (antenne…)
    min: vec2, max: vec2, layers: z.array(z.enum(['F', 'B'])).default(['F', 'B']), label: z.string().max(60).default(''),
  })).max(8).default([]),
  tags: z.array(z.string().max(30)).max(20).default([]),
  defaults: z.object({                          // valeurs reprises à la pose (pièce de catalogue)
    value: z.string().max(60).default(''),
    mpn: z.string().max(80).default(''),
    lcsc: z.string().max(20).default(''),
  }).optional(),
});

// ── Carte ──────────────────────────────────────────────────────

/** Élément de la carte mère sous la carte fille (dissipateur, entretoise, connecteur…). */
export const ObstacleSchema = z.object({
  label: z.string().max(80),
  kind: z.enum(['rect', 'circle']).default('rect'),
  min: vec2.optional(),                         // rect : coins
  max: vec2.optional(),
  c: vec2.optional(),                           // circle : centre et rayon
  r: num.positive().optional(),
  height: num.nonnegative(),                    // dépassement de la face de la carte mère tournée vers la carte (mm)
  note: z.string().max(300).default(''),
});

/** Carte mère sur laquelle s'enfiche la carte fille (repère commun). */
export const MotherSchema = z.object({
  name: z.string().max(80),
  outline: z.array(vec2).min(3).max(400),
  thickness: num.positive().default(1.6),
  side: z.enum(['below', 'above']).default('below'), // below : la mère est SOUS la carte (shield) ; above : au-dessus (carte porteuse)
  gap: num.positive(),                          // hauteur libre entre les deux cartes
  obstacles: z.array(ObstacleSchema).max(100).default([]),
});

export const BoardSchema = z.object({
  template: z.string().max(60).default('libre'),
  mother: MotherSchema.optional(),
  outline: z.array(vec2).min(3).max(400),       // polygone fermé (sens indifférent)
  thickness: num.positive().default(1.6),
  layers: z.union([z.literal(2), z.literal(4), z.literal(6)]).default(2),
  stackup: z.string().max(40).optional(),       // empilage JLCPCB (shared/stackup.js) — multicouche
  roles: z.record(z.string(), z.enum(['signal', 'plane'])).optional(), // rôle des couches internes (défaut : shared/layers.js)
  color: z.enum(['vert', 'bleu', 'rouge', 'noir', 'blanc', 'jaune', 'violet']).default('vert'),
  finish: z.enum(['HASL', 'HASL sans plomb', 'ENIG']).default('HASL sans plomb'),
});

export const RulesSchema = z.object({
  trackWidth: num.positive().default(0.25),     // piste signal
  powerWidth: num.positive().default(0.6),      // piste de puissance (nets de classe power)
  clearance: num.positive().default(0.2),       // isolement cuivre-cuivre
  edgeClearance: num.nonnegative().default(0.3),
  viaDrill: num.positive().default(0.3),
  viaDiameter: num.positive().default(0.6),
  minDrill: num.positive().default(0.3),
  grid: num.positive().default(0.25),           // pas du routeur automatique
  isolation: num.nonnegative().default(2.5),    // distance entre domaines isolés (nets à « domain »)
  pairTolerance: num.nonnegative().default(0.05), // écart intra-paire toléré autour de l'écart nominal
  pairSkew: num.nonnegative().default(0.13),    // différence de longueur intra-paire tolérée (mm)
});

// ── Placement et connexions ────────────────────────────────────

export const ComponentSchema = z.object({
  id: z.string().min(1),
  ref: z.string().min(1).max(16),               // repère (« U1 », « R3 », « J2 »)
  footprint: z.string().min(1).max(80),
  value: z.string().max(60).default(''),
  pos: vec2,
  rot: num.default(0),
  side: z.enum(['top', 'bottom']).default('top'),
  locked: z.boolean().default(false),           // posé par le gabarit (connecteurs de la carte mère)
  mpn: z.string().max(80).default(''),          // référence fabricant
  lcsc: z.string().max(20).default(''),         // référence LCSC (assemblage JLCPCB)
  dnp: z.boolean().default(false),              // non monté
  note: z.string().max(400).default(''),
});

export const NetSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(40),
  pins: z.array(z.string().max(40)).max(400),   // « REF.NUM » (le nom de broche est accepté à l'entrée)
  kind: z.enum(['signal', 'power']).default('signal'),
  width: num.positive().optional(),             // largeur imposée des pistes de ce net
  netclass: z.string().max(30).optional(),      // classe (shared/netclasses.js) : USB_SS, ETH_MDI…
  pair: z.string().max(40).optional(),          // net partenaire d'une paire différentielle
  impedance: num.positive().optional(),         // impédance visée (Ω), différentielle si pair
  domain: z.string().max(30).optional(),        // domaine isolé (ISO_CAN_A…) : règle rules.isolation
});

export const TrackSchema = z.object({
  id: z.string().min(1),
  net: z.string().max(40).default(''),          // nom du net
  layer: cuLayer,
  width: num.positive(),
  pts: z.array(vec2).min(2).max(2000),
  auto: z.boolean().default(false),             // posée par le routeur automatique
});

export const ViaSchema = z.object({
  id: z.string().min(1),
  net: z.string().max(40).default(''),
  pos: vec2,
  drill: num.positive(),
  diameter: num.positive(),
  auto: z.boolean().default(false),
});

export const ZoneSchema = z.object({
  id: z.string().min(1),
  net: z.string().max(40),
  layer: cuLayer,
  outline: z.array(vec2).min(3).max(400).optional(), // absent = toute la carte
  clearance: num.positive().optional(),             // défaut : règle + 0,1 mm
});

export const TextSchema = z.object({
  id: z.string().min(1),
  text: z.string().min(1).max(80),
  layer: z.enum(['F', 'B']).default('F'),        // sérigraphie dessus / dessous
  pos: vec2,
  size: num.positive().default(1.2),
  rot: num.default(0),
});

// ── Schéma (l'étude) ───────────────────────────────────────────
//
// Le schéma est la SOURCE de la netlist : project.nets est recalculé à partir des fils, des
// étiquettes et des symboles d'alimentation (shared/schematic.js). Repère du schéma : mm,
// Y vers le haut, grille de 2,54 mm.

export const SymbolPlacementSchema = z.object({
  comp: z.string().min(1),                      // id du composant
  pos: vec2,
  rot: z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]).default(0),
  mirror: z.boolean().default(false),
});

export const WireSchema = z.object({
  id: z.string().min(1),
  pts: z.array(vec2).min(2).max(200),
});

export const LabelSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(40),
  kind: z.enum(['net', 'power', 'gnd']).default('net'),
  pos: vec2.optional(),                         // étiquette posée sur un fil ou une broche…
  rot: num.default(0),
  at: z.object({ comp: z.string().min(1), pin: z.string().min(1).max(16) }).optional(), // …ou attachée à une broche
});

export const SchematicSchema = z.object({
  v: z.number().int().default(1),
  symbols: z.array(SymbolPlacementSchema).max(2000).default([]),
  wires: z.array(WireSchema).max(20000).default([]),
  labels: z.array(LabelSchema).max(20000).default([]),
  notes: z.array(z.object({ id: z.string().min(1), text: z.string().min(1).max(400), pos: vec2, size: num.positive().default(2) })).max(200).default([]),
});

export const ProjectSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(100),
  description: z.string().max(4000).default(''),
  updatedAt: z.string().optional(),
  owner: z.string().nullable().optional(),
  sharedWith: z.array(z.string()).default([]),
  viewers: z.array(z.string()).default([]),
  board: BoardSchema,
  rules: RulesSchema.default(RulesSchema.parse({})),
  components: z.array(ComponentSchema).max(2000).default([]),
  nets: z.array(NetSchema).max(2000).default([]),
  tracks: z.array(TrackSchema).max(20000).default([]),
  vias: z.array(ViaSchema).max(5000).default([]),
  zones: z.array(ZoneSchema).max(50).default([]),
  texts: z.array(TextSchema).max(500).default([]),
  footprints: z.array(FootprintSchema).max(300).default([]),
  schematic: SchematicSchema.optional(),        // absent = projet antérieur au schéma (migré au chargement)
});

export function validateProject(raw) {
  const r = ProjectSchema.safeParse(raw);
  if (r.success) return { ok: true, project: r.data };
  return { ok: false, errors: r.error.issues.slice(0, 20).map((i) => `${i.path.join('.')} : ${i.message}`) };
}

/** Rectangle de w × h aux coins arrondis de rayon r (polygone, origine en bas à gauche). */
export function roundedRect(w, h, r = 0, segs = 6) {
  if (r <= 0) return [[0, 0], [w, 0], [w, h], [0, h]];
  const pts = [];
  const corners = [[w - r, r, -90], [w - r, h - r, 0], [r, h - r, 90], [r, r, 180]];
  for (const [cx, cy, a0] of corners) {
    for (let k = 0; k <= segs; k++) {
      const a = ((a0 + (90 * k) / segs) * Math.PI) / 180;
      pts.push([round4(cx + r * Math.cos(a)), round4(cy + r * Math.sin(a))]);
    }
  }
  return pts;
}

export const round4 = (v) => Math.round(v * 1e4) / 1e4;

export function createEmptyProject(name) {
  return ProjectSchema.parse({
    id: newId('p'),
    name,
    board: { template: 'libre', outline: roundedRect(80, 60, 2) },
    schematic: { v: 1, symbols: [], wires: [], labels: [], notes: [] },
  });
}
